<template>
  <div class="dashboard-widget">
    <!-- Header Section -->
    <div class="flex justify-between items-center mb-4">
      <button
        class="p-button p-button-text"
        @click="viewYesterday"
      >
        <i class="pi pi-arrow-left mr-2" />
        Previous
      </button>
      <span class="text-lg font-semibold">{{ formattedDate }}</span>
      <button
        class="p-button p-button-text"
        @click="viewTomorrow"
      >
        Next
        <i class="pi pi-arrow-right ml-2" />
      </button>
    </div>

    <!-- Timeline View -->
    <div class="timeline-container flex">
      <!-- Time column -->
      <div class="time-column w-16 flex-shrink-0 pr-2">
        <div
          v-for="time in timeSlots"
          :key="time.label"
          class="time-slot"
          :style="{ height: `${timeSlotHeight}px` }"
        >
          <span class="text-xs text-gray-500">{{ time.label }}</span>
        </div>
      </div>
            
      <!-- Jobs column -->
      <div class="jobs-column flex-grow relative">
        <!-- Time slot dividers -->
        <div
          v-for="time in timeSlots"
          :key="`divider-${time.label}`" 
          class="time-divider border-t border-gray-200" 
          :style="{ top: `${time.position}px` }"
        />
                
        <!-- Job cards positioned based on time -->
        <div
          v-for="timeSlot in groupedTimelineJobs"
          :key="timeSlot.time" 
          class="absolute w-full transition-all duration-200 px-1"
          :style="{ top: `${timeSlot.position}px` }"
        >
          <!-- Multiple jobs in the same time slot -->
          <div class="flex gap-2 w-full">
            <Card
              v-for="(job, jobIndex) in timeSlot.jobs"
              :key="`${timeSlot.time}-${jobIndex}`"
              class="cursor-pointer hover:shadow-lg transition-shadow duration-200 border border-gray-200"
              :class="{ 'w-full': timeSlot.jobs.length === 1, 'w-1/2': timeSlot.jobs.length === 2, 'w-1/3': timeSlot.jobs.length === 3, 'w-1/4': timeSlot.jobs.length >= 4 }"
              @click="linkProject(job.id)"
            >
              <template #content>
                <div class="flex justify-between items-center">
                  <div class="flex-1">
                    <div class="flex items-center gap-4">
                      <div class="flex-1">
                        <div class="flex items-center gap-2">
                          <span class="font-bold">{{ job.time }}</span>
                          <Badge
                            :value="job.type"
                            :severity="getJobSeverity(job.type)"
                          />
                        </div>
                        <h3 class="text-lg font-semibold text-gray-800 mt-1">
                          {{ job.name }}
                        </h3>
                        <div class="flex items-center gap-2 mt-1">
                          <i class="pi pi-building text-gray-500" />
                          <span class="text-sm text-gray-600">{{ job.client }}</span>
                        </div>
                        <p class="text-sm text-gray-500 mt-1">
                          {{ job.jobNumber }}
                        </p>
                      </div>
                      <div class="flex items-center gap-6">
                        <div class="flex items-center gap-2">
                          <i
                            :class="job.icon"
                            class="text-gray-500"
                          />
                          <span class="text-sm text-gray-600">{{ job.items }} items</span>
                        </div>
                        <div class="flex items-center gap-2">
                          <i
                            :class="job.arrowIcon"
                            class="text-gray-500"
                          />
                          <span class="text-sm text-gray-600">{{ isIncomingJob(job) ? 'Incoming' : 'Outgoing' }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </Card>
          </div>
        </div>
      </div>
    </div>
        
    <!-- Empty state when no jobs for the day -->
    <div
      v-if="timelineJobs.length === 0"
      class="text-center py-8 bg-white rounded-lg shadow mt-4"
    >
      <i class="pi pi-calendar-times text-5xl text-gray-300 mb-3" />
      <p class="text-gray-500">
        No warehouse activities scheduled for this day
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from 'vue';
import { format, addDays, subDays } from 'date-fns';
import { useRouter } from 'vue-router';

// Define props for accepting projects from parent
const props = defineProps({
    projects: {
        type: Array,
        default: () => []
    }
});

const timelineJobs = ref([]);
const currentViewDate = ref(new Date());
const router = useRouter();

// Define time slots for the timeline
const startHour = 7; // 7 AM
const endHour = 19; // 7 PM
const timeSlotHeight = 80; // height in pixels for each hour

// Generate time slots for the timeline
const timeSlots = computed(() => {
    const slots = [];
    for (let hour = startHour; hour <= endHour; hour++) {
        const position = (hour - startHour) * timeSlotHeight;
        slots.push({
            hour,
            label: `${hour % 12 || 12}${hour < 12 ? 'am' : 'pm'}`,
            position
        });
    }
    return slots;
});

// Calculate position for a job based on its time
const getJobPosition = (job) => {
    try {
        // Parse the time (format: "HH:mm")
        const [hours, minutes] = job.time.split(':').map(Number);
        
        // Calculate position
        const hourPosition = (hours - startHour) * timeSlotHeight;
        const minuteOffset = (minutes / 60) * timeSlotHeight;
        
        return hourPosition + minuteOffset;
    } catch (e) {
        console.error("Error calculating job position:", e);
        return 0; // Fallback to top position
    }
};

const linkProject = (id) => {
    router.push(`/warehouse-project/${id}`);
};

// Helper to determine job alignment
const isIncomingJob = job =>
    ['Customer Return', 'Truck Unload', 'Other In'].includes(job.type);

const isOutgoingJob = job =>
    ['Customer Collection', 'Truck Load', 'Other Out'].includes(job.type);

// Get severity for job type
const getJobSeverity = (type) => {
    if (isIncomingJob({ type })) return 'info';
    if (isOutgoingJob({ type })) return 'success';
    return 'secondary';
};

// Format current view date
const formattedDate = computed(() =>
    format(currentViewDate.value, 'EEEE do MMMM') // Format: "Monday 34th August"
);

// Change the date for the view
const viewYesterday = () => {
    currentViewDate.value = subDays(currentViewDate.value, 1);
    regenerateTimeline();
};

const viewTomorrow = () => {
    currentViewDate.value = addDays(currentViewDate.value, 1);
    regenerateTimeline();
};

// Realistic event templates
const eventTemplates = [
    {
        name: 'Music Festival Setup',
        type: 'Truck Load',
        items: 125,
        client: 'SoundWave Events'
    },
    {
        name: 'Corporate Conference Return',
        type: 'Customer Return',
        items: 68,
        client: 'TechCorp Inc.'
    },
    {
        name: 'Wedding Equipment Delivery',
        type: 'Customer Collection',
        items: 32,
        client: 'Elite Wedding Planners'
    },
    {
        name: 'Theater Production Pickup',
        type: 'Truck Unload',
        items: 95,
        client: 'City Arts Center'
    },
    {
        name: 'Sports Event Equipment',
        type: 'Truck Load',
        items: 147,
        client: 'Regional Sports Authority'
    },
    {
        name: 'Charity Gala Setup',
        type: 'Customer Collection',
        items: 42,
        client: 'Hope Foundation'
    },
    {
        name: 'Product Launch Equipment',
        type: 'Truck Load',
        items: 83,
        client: 'Innovate Products'
    },
    {
        name: 'Festival Teardown',
        type: 'Truck Unload',
        items: 110,
        client: 'City Entertainment'
    },
    {
        name: 'Exhibition Equipment Return',
        type: 'Customer Return',
        items: 75,
        client: 'Trade Show Productions'
    },
    {
        name: 'Audio Equipment Maintenance',
        type: 'Other In',
        items: 22,
        client: 'Internal'
    },
    {
        name: 'Lighting Rig Transportation',
        type: 'Other Out',
        items: 35,
        client: 'Partner Venue'
    }
];

// Generate a full day's worth of projects
const generateFullDayProjects = () => {
    // If projects are provided via props, use them instead
    if (props.projects && props.projects.length > 0) {
        console.log("Using provided projects: ", props.projects);
        
        // Only include confirmed projects
        const confirmedProjects = props.projects.filter(project => 
            project.status === 'confirmed' || !project.status
        );
        console.log("Confirmed projects: ", confirmedProjects);
        
        // Map data to correct format
        return confirmedProjects.map(project => ({
            id: project.id,
            name: project.name || project.projectName || 'Unnamed Project',
            startTime: project.startTime ? new Date(project.startTime) : 
                      project.start ? new Date(project.start) : new Date(),
            type: project.type || 'Truck Unload', // Default to incoming type for consistency
            items: project.items || project.totalItems || project.itemsCount || 50,
            client: project.client || 'Client Name',
            time: project.time || format(new Date(project.startTime || project.start || new Date()), 'HH:mm')
        }));
    }
    
    console.log("No projects provided, generating fake data");
    // Otherwise, generate fake data
    // Generate a realistic number of events for the day based on day of week
    // Weekends might be busier for events
    const dayOfWeek = currentViewDate.value.getDay(); // 0 = Sunday, 6 = Saturday
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const count = isWeekend ? 5 + Math.floor(Math.random() * 4) : 2 + Math.floor(Math.random() * 5);
    
    // Use time slots that make sense for a warehouse
    const timeSlots = [
        { hour: 7, minute: 30 }, // Early morning
        { hour: 8, minute: 0 },
        { hour: 8, minute: 30 },
        { hour: 9, minute: 0 },
        { hour: 10, minute: 0 },
        { hour: 11, minute: 30 },
        { hour: 13, minute: 0 }, // After lunch
        { hour: 14, minute: 0 },
        { hour: 15, minute: 30 },
        { hour: 16, minute: 0 },
        { hour: 17, minute: 0 }, // End of day
    ];
    
    // Randomly select events from templates with no duplicates
    const usedEventIndexes = new Set();
    return Array.from({ length: count }).map((_, i) => {
        // Select a random event template without repeating
        let eventIndex;
        do {
            eventIndex = Math.floor(Math.random() * eventTemplates.length);
        } while (usedEventIndexes.has(eventIndex) && usedEventIndexes.size < eventTemplates.length);
        usedEventIndexes.add(eventIndex);
        
        const event = eventTemplates[eventIndex];
        
        // Select a time slot
        const timeSlot = timeSlots[Math.floor(Math.random() * timeSlots.length)];
        
        return {
            id: `${i + 1}`,
            name: event.name,
            startTime: new Date(
                currentViewDate.value.getFullYear(), 
                currentViewDate.value.getMonth(), 
                currentViewDate.value.getDate(), 
                timeSlot.hour, 
                timeSlot.minute
            ),
            type: event.type,
            items: event.items,
            client: event.client
        };
    });
};

const regenerateTimeline = () => {
    const projects = generateFullDayProjects();
    console.log("Generated projects for timeline: ", projects);

    if (!projects || projects.length === 0) {
        console.log("No projects available for the timeline");
        timelineJobs.value = [];
        return;
    }

    // Ensure each project has required fields
    timelineJobs.value = projects.map((project) => {
        // Make sure we have a valid project object
        if (!project) {
            console.error("Invalid project object:", project);
            return null;
        }
        
        // Determine job type if not provided
        const type = project.type || 'Truck Load';
        
        return {
            id: project.id,
            startTime: project.startTime,
            time: project.time || (project.startTime ? format(project.startTime, 'HH:mm') : '00:00'),
            jobNumber: project.jobNumber || project.projectNumber || generateJobNumber(),
            icon: determineJobIcon(type),
            arrowIcon: determineArrowIcon(type),
            type: type,
            name: project.name || 'Unnamed Project',
            items: project.items || 50,
            client: project.client || 'Client'
        };
    })
    .filter(job => job !== null) // Remove any invalid projects
    .sort((a, b) => {
        // Sort by time string if startTime is not available
        if (!a.startTime || !b.startTime) {
            return a.time.localeCompare(b.time);
        }
        return a.startTime - b.startTime;
    }); 
    
    console.log("Timeline jobs after processing: ", timelineJobs.value);
};

// Mock data for when no real projects are available
const mockProjects = [
    {
        id: '1',
        time: '09:30',
        jobNumber: 'WH-1001',
        icon: 'pi pi-truck',
        arrowIcon: 'pi pi-arrow-left',
        type: 'Truck Unload',
        name: 'Festival Equipment Return',
        items: 75,
        client: 'SoundWave Events',
        severity: 'info'
    },
    {
        id: '2',
        time: '11:00',
        jobNumber: 'WH-1002',
        icon: 'pi pi-user',
        arrowIcon: 'pi pi-arrow-left',
        type: 'Customer Return',
        name: 'Corporate Conference Equipment',
        items: 42,
        client: 'TechCorp Inc.',
        severity: 'info'
    },
    {
        id: '3',
        time: '14:30',
        jobNumber: 'WH-1003',
        icon: 'pi pi-truck',
        arrowIcon: 'pi pi-arrow-left',
        type: 'Truck Unload',
        name: 'Theater Production Materials',
        items: 120,
        client: 'City Arts Center',
        severity: 'info'
    },
    {
        id: '4',
        time: '16:00',
        jobNumber: 'WH-1004',
        icon: 'pi pi-box',
        arrowIcon: 'pi pi-arrow-left',
        type: 'Other In',
        name: 'Equipment Maintenance Return',
        items: 35,
        client: 'Internal Maintenance',
        severity: 'info'
    }
];

onMounted(() => {
    console.log("TodayComponent mounted with projects:", props.projects);
    regenerateTimeline();
    
    // Filter to show only incoming-type projects if timelineJobs contains mixed types
    if (timelineJobs.value.length > 0) {
        const incomingTypes = ['Customer Return', 'Truck Unload', 'Other In'];
        const hasOutgoingTypes = timelineJobs.value.some(job => 
            !incomingTypes.includes(job.type) && 
            !job.type.toLowerCase().includes('in') && 
            !job.type.toLowerCase().includes('return') && 
            !job.type.toLowerCase().includes('unload')
        );
        
        if (hasOutgoingTypes) {
            console.log("Found outgoing types in timeline, filtering to show only incoming types");
            timelineJobs.value = timelineJobs.value.filter(job => 
                incomingTypes.includes(job.type) || 
                job.type.toLowerCase().includes('in') || 
                job.type.toLowerCase().includes('return') || 
                job.type.toLowerCase().includes('unload')
            );
        }
    }
    
    // Always display mock data if no projects are in the timeline
    if (timelineJobs.value.length === 0) {
        console.log("No projects available, using mock data");
        timelineJobs.value = mockProjects;
        console.log("Using mock timeline jobs:", timelineJobs.value);
    }
});

// Generate a more realistic job number
const generateJobNumber = () => {
    const year = new Date().getFullYear().toString().substring(2);
    const month = (new Date().getMonth() + 1).toString().padStart(2, '0');
    const seq = Math.floor(100 + Math.random() * 900);
    return `WH${year}${month}-${seq}`;
};

// Determine icons and styles
const determineJobIcon = type => {
    switch (type) {
        case 'Customer Collection':
        case 'Customer Return':
            return 'pi pi-user'; // User icon
        case 'Truck Load':
        case 'Truck Unload':
            return 'pi pi-truck'; // Truck icon
        case 'Other In':
        case 'Other Out':
            return 'pi pi-box'; // Box icon
        default:
            return 'pi pi-question'; // Fallback
    }
};

const determineArrowIcon = type => {
    switch (type) {
        case 'Truck Load':
        case 'Other Out':
        case 'Customer Collection':
            return 'pi pi-arrow-right'; // Outgoing arrow
        case 'Truck Unload':
        case 'Other In':
        case 'Customer Return':
            return 'pi pi-arrow-left'; // Incoming arrow
        default:
            return 'pi pi-question'; // Fallback
    }
};

// Group jobs by time to handle multiple jobs at the same time
const groupedTimelineJobs = computed(() => {
    const grouped = {};
    
    // Group jobs by their time
    timelineJobs.value.forEach(job => {
        if (!grouped[job.time]) {
            grouped[job.time] = [];
        }
        grouped[job.time].push(job);
    });
    
    // Convert the object to an array of time slots with jobs
    return Object.entries(grouped).map(([time, jobs]) => ({
        time,
        jobs,
        position: getJobPosition({ time })
    })).sort((a, b) => a.position - b.position);
});
</script>

<style scoped>
.dashboard-widget {
    padding: 1rem;
}

.timeline-container {
    min-height: 800px;
    position: relative;
}

.time-column {
    position: relative;
    z-index: 10;
}

.time-slot {
    position: relative;
    display: flex;
    align-items: flex-start;
    justify-content: flex-end;
}

.jobs-column {
    overflow-y: visible;
}

.time-divider {
    position: absolute;
    width: 100%;
    z-index: 5;
}
</style>
